import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  AccountTree as WorkflowIcon,
} from '@mui/icons-material';
import { ticketsApi, dokumenteApi, emailsApi, webhooksApi } from '../../services/api';
import workflowService from '../../services/workflowService';
import { getApiBaseUrl } from '../../utils/apiUrl';
import type {
  TicketDialogProps,
  TicketFormData,
  TicketDocument,
  TicketEmail,
  Webhook,
  WorkflowDto,
  GeneriertesDokument,
  Email,
} from './types';

// Import sub-components
import TabPanel from './TabPanel';
import TicketDetailsTab from './TicketDetailsTab';
import TicketEmailTab from './TicketEmailTab';
import TicketWorkflowTab from './TicketWorkflowTab';
import TicketDocumentsTab from './TicketDocumentsTab';

const INITIAL_FORM_DATA: TicketFormData = {
  titel: '',
  was: '',
  wie: '',
  wann: '',
  status: 'Offen',
  prioritaet: 'Mittel',
  zugewiesenAn: undefined,
  adresseID: undefined,
  milestoneID: undefined,
  autoExecute: false,
  webhookID: undefined,
  wiederholungAktiv: false,
  wiederholungIntervall: undefined,
  wiederholungEinheit: 'Tage',
  wiederholungAnzahl: undefined,
};

const TicketDialog: React.FC<TicketDialogProps> = ({
  open,
  ticket,
  users,
  adressen,
  milestones,
  onClose,
  onSave,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState<TicketFormData>(INITIAL_FORM_DATA);

  // Webhook state
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);

  // Documents & Email state
  const [documents, setDocuments] = useState<TicketDocument[]>([]);
  const [email, setEmail] = useState<TicketEmail | null>(null);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [allDocuments, setAllDocuments] = useState<GeneriertesDokument[]>([]);
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [allEmails, setAllEmails] = useState<Email[]>([]);
  const [showAddEmail, setShowAddEmail] = useState(false);

  // Workflow state
  const [workflow, setWorkflow] = useState<WorkflowDto | null>(null);
  const [loadingWorkflow, setLoadingWorkflow] = useState(false);

  useEffect(() => {
    if (ticket) {
      setFormData({
        titel: ticket.titel,
        was: ticket.was,
        wie: ticket.wie || '',
        wann: ticket.wann ? ticket.wann.substring(0, 16) : '',
        status: ticket.status,
        prioritaet: ticket.prioritaet || 'Mittel',
        zugewiesenAn: ticket.zugewiesenAn,
        adresseID: ticket.adresseID,
        milestoneID: ticket.milestoneID,
        autoExecute: ticket.autoExecute || false,
        webhookID: ticket.webhookID,
        wiederholungAktiv: ticket.wiederholungAktiv || false,
        wiederholungIntervall: ticket.wiederholungIntervall,
        wiederholungEinheit: ticket.wiederholungEinheit || 'Tage',
        wiederholungAnzahl: ticket.wiederholungAnzahl,
      });

      loadDocuments();
      loadEmail();

      if (ticket.workflowID) {
        loadWorkflow(ticket.workflowID);
      } else {
        setWorkflow(null);
      }
    } else {
      setFormData(INITIAL_FORM_DATA);
      setDocuments([]);
      setEmail(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket, open]);

  useEffect(() => {
    if (open) {
      loadWebhooks();
    }
  }, [open]);

  // Data loading functions
  const loadWebhooks = async () => {
    setLoadingWebhooks(true);
    try {
      const response = await webhooksApi.getAll();
      setWebhooks(response.data.filter((w: Webhook) => w.aktiv));
    } catch (error) {
      console.error('Fehler beim Laden der Webhooks:', error);
    } finally {
      setLoadingWebhooks(false);
    }
  };

  const loadDocuments = async () => {
    if (!ticket) return;
    setLoadingDocs(true);
    try {
      const response = await ticketsApi.getDocuments(ticket.ticketID);
      setDocuments(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Dokumente:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const loadEmail = async () => {
    if (!ticket) return;
    setLoadingEmail(true);
    try {
      const response = await ticketsApi.getEmail(ticket.ticketID);
      setEmail(response.data);
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } };
      if (err.response?.status !== 404) {
        console.error('Fehler beim Laden der E-Mail:', error);
      }
    } finally {
      setLoadingEmail(false);
    }
  };

  const loadWorkflow = async (workflowId: number) => {
    setLoadingWorkflow(true);
    try {
      const workflowData = await workflowService.getById(workflowId);
      setWorkflow(workflowData);
    } catch (error) {
      console.error('Fehler beim Laden des Workflows:', error);
      setWorkflow(null);
    } finally {
      setLoadingWorkflow(false);
    }
  };

  const loadAvailableDocuments = async () => {
    try {
      const response = await dokumenteApi.getAll();
      setAllDocuments(response.data);
      setShowAddDocument(true);
    } catch (error) {
      console.error('Fehler beim Laden der Dokumente:', error);
    }
  };

  const loadAvailableEmails = async () => {
    try {
      const response = await emailsApi.getAll({ take: 100 });
      const activeEmails = response.data.filter((email: Email) => !email.archiviert);
      setAllEmails(activeEmails);
      setShowAddEmail(true);
    } catch (error) {
      console.error('Fehler beim Laden der E-Mails:', error);
    }
  };

  // Document handlers
  const handleLinkDocument = async (dokumentId: number) => {
    if (!ticket) return;
    try {
      await ticketsApi.linkDocument(ticket.ticketID, dokumentId);
      setShowAddDocument(false);
      loadDocuments();
    } catch (error) {
      console.error('Fehler beim Verknüpfen des Dokuments:', error);
    }
  };

  const handleUnlinkDocument = async (dokumentId: number) => {
    if (!ticket) return;
    try {
      await ticketsApi.unlinkDocument(ticket.ticketID, dokumentId);
      loadDocuments();
    } catch (error) {
      console.error('Fehler beim Entfernen der Verknüpfung:', error);
    }
  };

  const handleDownloadDocument = (dokumentId: number) => {
    const backendUrl = getApiBaseUrl();
    window.open(`${backendUrl}/api/dokumente/${dokumentId}/download`, '_blank');
  };

  const handleNavigateToDocument = (dokumentId: number) => {
    window.location.href = `/dokumente?edit=${dokumentId}`;
  };

  // Email handlers
  const handleOpenEmail = () => {
    if (email) {
      window.location.href = `/emails?id=${email.emailID}`;
    }
  };

  const handleReplyToEmail = () => {
    if (email) {
      window.location.href = `/emails?id=${email.emailID}&reply=true`;
    }
  };

  const handleLinkEmail = async (emailId: number) => {
    if (!ticket) return;
    try {
      await ticketsApi.linkEmail(ticket.ticketID, emailId);
      setShowAddEmail(false);
      await loadEmail();
      alert('E-Mail erfolgreich verknüpft!');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      console.error('Fehler beim Verknüpfen der E-Mail:', error);
      alert('Fehler beim Verknüpfen: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUnlinkEmail = async () => {
    if (!ticket) return;
    if (!window.confirm('E-Mail-Verknüpfung wirklich entfernen?')) return;
    try {
      await ticketsApi.unlinkEmail(ticket.ticketID);
      setEmail(null);
      alert('E-Mail-Verknüpfung erfolgreich entfernt!');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      console.error('Fehler beim Entfernen der E-Mail-Verknüpfung:', error);
      alert('Fehler beim Entfernen: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSave = async () => {
    await onSave(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        {ticket ? 'Ticket bearbeiten' : 'Neues Ticket'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Details" />
          {ticket && <Tab label="E-Mail" icon={email ? <EmailIcon color="primary" /> : undefined} iconPosition="end" />}
          {ticket && ticket.workflowID && <Tab label="Workflow" icon={<WorkflowIcon color="primary" />} iconPosition="end" />}
          {ticket && <Tab label={`Dokumente (${documents.length})`} />}
        </Tabs>
      </Box>

      <DialogContent>
        {/* Details Tab */}
        <TabPanel value={tabValue} index={0}>
          <TicketDetailsTab
            formData={formData}
            onFormChange={setFormData}
            users={users}
            adressen={adressen}
            milestones={milestones}
            webhooks={webhooks}
            loadingWebhooks={loadingWebhooks}
            ticket={ticket}
          />
        </TabPanel>

        {/* E-Mail Tab */}
        {ticket && (
          <TabPanel value={tabValue} index={1}>
            <TicketEmailTab
              ticket={ticket}
              email={email}
              loadingEmail={loadingEmail}
              onUnlinkEmail={handleUnlinkEmail}
              onOpenEmail={handleOpenEmail}
              onReplyToEmail={handleReplyToEmail}
              onLinkEmail={handleLinkEmail}
              allEmails={allEmails}
              showAddEmail={showAddEmail}
              onShowAddEmail={loadAvailableEmails}
              onHideAddEmail={() => setShowAddEmail(false)}
            />
          </TabPanel>
        )}

        {/* Workflow Tab */}
        {ticket && ticket.workflowID && (
          <TabPanel value={tabValue} index={2}>
            <TicketWorkflowTab
              ticket={ticket}
              workflow={workflow}
              loadingWorkflow={loadingWorkflow}
            />
          </TabPanel>
        )}

        {/* Dokumente Tab */}
        {ticket && (
          <TabPanel value={tabValue} index={ticket.workflowID ? 3 : 2}>
            <TicketDocumentsTab
              ticket={ticket}
              documents={documents}
              loadingDocs={loadingDocs}
              onUnlinkDocument={handleUnlinkDocument}
              onLinkDocument={handleLinkDocument}
              onDownloadDocument={handleDownloadDocument}
              onNavigateToDocument={handleNavigateToDocument}
              allDocuments={allDocuments}
              showAddDocument={showAddDocument}
              onShowAddDocument={loadAvailableDocuments}
              onHideAddDocument={() => setShowAddDocument(false)}
            />
          </TabPanel>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button onClick={handleSave} variant="contained">
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketDialog;
